import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Star, Download, CheckCircle2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { SiGoogleplay, SiApple } from 'react-icons/si';
import { projects, personalProjects } from '../data/mock';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import Header from './Header';
import Footer from './Footer';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const allProjects = [...projects, ...personalProjects];
  const project = allProjects.find((p) => p.id === id);
  const [expandedChallenge, setExpandedChallenge] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Project not found</h1>
          <Button onClick={() => navigate('/')}>Go Back Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-b from-[#0f0f0f] to-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="mb-8 text-gray-400 hover:text-white hover:bg-white/5"
            >
              <ArrowLeft className="mr-2" size={18} />
              Back to Projects
            </Button>
          </motion.div>

          {/* Project Header with Image */}
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Left: Project Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex-1"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-3 text-white">{project.name}</h1>
              <p className="text-xl text-gray-400 mb-6">{project.tagline}</p>
              
              {/* Metrics & Links */}
              <div className="flex flex-wrap items-center gap-6 mb-6">
                {project.downloads !== 'N/A' && (
                  <div className="flex items-center gap-2">
                    <Download size={20} className="text-gray-400" />
                    <span className="font-semibold text-white">{project.downloads}</span>
                  </div>
                )}
                {project.rating !== 'N/A' && (
                  <div className="flex items-center gap-2">
                    <Star size={20} className="text-gray-400" fill="currentColor" />
                    <span className="font-semibold text-white">{project.rating}</span>
                  </div>
                )}
              </div>

              {/* Store Badges */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                {project.playStoreUrl !== '' && (
                  <motion.a
                    href={project.playStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-3 px-5 py-2.5 bg-[#01875f] hover:bg-[#019f6e] text-white rounded-lg font-semibold transition-colors text-sm"
                  >
                    <SiGoogleplay size={20} />
                    <span>Play Store</span>
                  </motion.a>
                )}
                {project.appStoreUrl !== '' && (
                  <motion.a
                    href={project.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-3 px-5 py-2.5 bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-lg font-semibold transition-colors text-sm"
                  >
                    <SiApple size={20} />
                    <span>App Store</span>
                  </motion.a>
                )}
              </div>

              {/* Tech Stack */}
              <div className="flex flex-wrap gap-2">
                {project.techStack.map((tech) => (
                  <Badge key={tech} className="bg-[#2a2a2a] text-gray-300 border-0 px-3 py-1">
                    {tech}
                  </Badge>
                ))}
              </div>
            </motion.div>

            {/* Right: Hero Image */}
            {project.heroImage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:w-2/5 order-first lg:order-last"
              >
                <div className="rounded-2xl overflow-hidden border-2 border-[#2a2a2a] shadow-2xl">
                  <img
                    src={project.heroImage}
                    alt={project.name}
                    className="w-full h-auto object-cover"
                  />
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Product Overview */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-6 text-white"
          >
            Product Overview
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-6 h-full">
                <div className="flex items-center gap-3 mb-3">
                  <AlertCircle size={20} className="text-orange-400" />
                  <h3 className="text-xl font-semibold text-white">Problem</h3>
                </div>
                <p className="text-gray-400 leading-relaxed">{project.problem}</p>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-6 h-full">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle2 size={20} className="text-green-400" />
                  <h3 className="text-xl font-semibold text-white">Solution</h3>
                </div>
                <p className="text-gray-400 leading-relaxed">{project.solution}</p>
              </Card>
            </motion.div>
          </div>

          {/* Impact Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-semibold mb-4 text-white">Impact & Results</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {project.impact.map((metric, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-[#2a2a2a] p-4 hover:border-[#404040] transition-colors">
                    <p className="text-gray-200 font-medium leading-relaxed text-sm">{metric}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* System Architecture */}
      <section className="py-12 px-6 bg-[#0f0f0f]">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-6 text-white"
          >
            System Design & Architecture
          </motion.h2>
          
          {/* Architecture Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-6 mb-6">
              <h3 className="text-xl font-semibold mb-3 text-white">Architecture Overview</h3>
              <p className="text-gray-400 mb-4">{project.architecture.overview}</p>
              <div className="space-y-3">
                {project.architecture.layers.map((layer, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-white rounded-full flex-shrink-0"></div>
                    <span className="text-gray-300 text-sm">{layer}</span>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Scalability & Performance */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-6 h-full">
                <h3 className="text-xl font-semibold mb-3 text-white">Scalability</h3>
                <p className="text-gray-400 leading-relaxed">{project.architecture.scalability}</p>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-6 h-full">
                <h3 className="text-xl font-semibold mb-3 text-white">Performance</h3>
                <p className="text-gray-400 leading-relaxed">{project.architecture.performance}</p>
              </Card>
            </motion.div>
          </div>

          {/* Technical Challenges - Accordion */}
          {project.challenges && project.challenges.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-semibold mb-4 text-white">Technical Challenges & Solutions</h3>
              <div className="space-y-4">
                {project.challenges.map((challenge, index) => (
                  <Card
                    key={index}
                    className="bg-[#1a1a1a] border-[#2a2a2a] overflow-hidden transition-all"
                  >
                    <button
                      onClick={() => setExpandedChallenge(expandedChallenge === index ? null : index)}
                      className="w-full p-4 flex items-center justify-between hover:bg-[#202020] transition-colors text-left"
                    >
                      <h4 className="text-lg font-semibold text-white">{challenge.title}</h4>
                      {expandedChallenge === index ? (
                        <ChevronUp size={20} className="text-gray-400" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400" />
                      )}
                    </button>
                    {expandedChallenge === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="px-4 pb-4 space-y-4"
                      >
                        <p className="text-gray-400 text-sm leading-relaxed">{challenge.description}</p>
                        <div className="pl-3 border-l-2 border-green-500/50 bg-green-500/5 p-3 rounded-r-lg">
                          <p className="text-xs text-gray-500 mb-2 monospace font-semibold">Solution:</p>
                          <p className="text-gray-300 text-sm leading-relaxed">{challenge.solution}</p>
                        </div>
                      </motion.div>
                    )}
                  </Card>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectDetail;
